import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { ServicePageData } from '../../../mocks/servicePages';

interface ServiceContentProps {
  service: ServicePageData;
}

export default function ServiceContent({ service }: ServiceContentProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="space-y-12">
      {/* What Is This Service */}
      <section>
        <h2 className="text-2xl sm:text-3xl font-black text-[#0f1f3d] mb-5 pb-3 border-b-2 border-cyan-500">
          What Is {service.title.split(' in Las Vegas')[0]}?
        </h2>
        <div className="space-y-4">
          {service.intro.map((para, i) => (
            <p key={i} className="text-gray-700 text-base leading-relaxed">{para}</p>
          ))}
        </div>

        {/* Internal Link Blurbs */}
        {service.internalLinkBlurbs && service.internalLinkBlurbs.length > 0 && (
          <div className="mt-6 space-y-3 border-l-4 border-cyan-400 pl-4">
            {service.internalLinkBlurbs.map((blurb, i) => (
              <p key={i} className="text-gray-600 text-sm leading-relaxed">
                {blurb.before}{' '}
                <Link
                  to={`/${blurb.slug}`}
                  className="text-cyan-600 font-semibold underline underline-offset-2 hover:text-cyan-700 transition-colors"
                >
                  {blurb.linkText}
                </Link>
                {blurb.after ? ` ${blurb.after}` : ''}
              </p>
            ))}
          </div>
        )}
      </section>

      {/* Signs You Need It */}
      <section>
        <h2 className="text-2xl sm:text-3xl font-black text-[#0f1f3d] mb-5 pb-3 border-b-2 border-cyan-500">
          Signs You Need This Service
        </h2>
        <p className="text-gray-600 mb-5 text-base">
          Don't wait until the problem becomes a catastrophe. If you're experiencing any of the following signs, call our Las Vegas specialists today for same-day service:
        </p>
        <ul className="space-y-3">
          {service.signs.map((sign, i) => (
            <li key={i} className="flex items-start gap-3 bg-red-50 border-l-4 border-red-500 px-4 py-3 rounded-r-lg">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-alert-fill text-red-600 text-lg"></i>
              </div>
              <span className="text-gray-800 text-sm sm:text-base font-medium">{sign}</span>
            </li>
          ))}
        </ul>
        <div className="mt-6 bg-[#0f1f3d] rounded-xl px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white font-semibold text-sm sm:text-base text-center sm:text-left">
            Recognizing any of these? Don't wait — call now for same-day service in Las Vegas.
          </p>
          <a
            href="tel:7252675482"
            className="bg-red-600 text-white px-6 py-3 rounded-lg font-black text-sm whitespace-nowrap hover:bg-red-500 transition-colors flex items-center gap-2 flex-shrink-0"
          >
            <i className="ri-phone-fill"></i> Call Now
          </a>
        </div>
      </section>

      {/* How We Fix It */}
      <section>
        <h2 className="text-2xl sm:text-3xl font-black text-[#0f1f3d] mb-5 pb-3 border-b-2 border-cyan-500">
          Our Process
        </h2>
        <p className="text-gray-600 mb-6 text-base">
          When you call Leak Detectives for {service.title.split(' in Las Vegas')[0].toLowerCase()} in Las Vegas, here's exactly what to expect:
        </p>
        <div className="space-y-4">
          {service.howWeFixIt.map((step, i) => (
            <div key={i} className="flex items-start gap-4 bg-white border border-gray-200 rounded-xl p-5">
              <div className="w-10 h-10 flex items-center justify-center bg-cyan-500 text-white rounded-full font-black text-lg flex-shrink-0">
                {i + 1}
              </div>
              <div>
                <h3 className="font-bold text-[#0f1f3d] text-base sm:text-lg mb-1">{step.title}</h3>
                <p className="text-gray-600 text-sm sm:text-base leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section>
        <h2 className="text-2xl sm:text-3xl font-black text-[#0f1f3d] mb-5 pb-3 border-b-2 border-cyan-500">
          Why Choose Leak Detectives in Las Vegas
        </h2>
        <p className="text-gray-600 mb-6 text-base">
          Las Vegas homeowners trust Leak Detectives because we combine professional equipment, honest pricing, and real field experience — licensed and insured every step of the way.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {service.benefits.map((benefit, i) => (
            <div key={i} className="flex items-start gap-3 bg-gradient-to-br from-[#0a1628] to-[#162447] rounded-xl px-4 py-4">
              <div className="w-8 h-8 flex items-center justify-center bg-cyan-500 rounded-lg flex-shrink-0">
                <i className="ri-check-line text-white text-base"></i>
              </div>
              <p className="text-white text-sm font-medium leading-relaxed">{benefit}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section>
        <h2 className="text-2xl sm:text-3xl font-black text-[#0f1f3d] mb-5 pb-3 border-b-2 border-cyan-500">
          Frequently Asked Questions
        </h2>
        <div className="space-y-3">
          {service.faqs.map((faq, i) => (
            <div
              key={i}
              className="border border-gray-200 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 bg-white hover:bg-gray-50 transition-colors cursor-pointer text-left"
              >
                <span className="font-bold text-[#0f1f3d] text-sm sm:text-base">{faq.q}</span>
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className={`text-cyan-500 text-xl transition-transform duration-200 ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                </div>
              </button>
              {openFaq === i && (
                <div className="px-5 py-4 bg-gray-50 border-t border-gray-200">
                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Related Services */}
      {service.relatedServices.length > 0 && (
        <section>
          <h2 className="text-2xl sm:text-3xl font-black text-[#0f1f3d] mb-5 pb-3 border-b-2 border-cyan-500">
            Related Services
          </h2>
          <p className="text-gray-600 mb-5 text-base">
            We provide a full range of leak detection and plumbing services throughout Las Vegas. Explore related services:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {service.relatedServices.map((rel) => (
              <Link
                key={rel.slug}
                to={`/${rel.slug}`}
                className="flex items-center gap-3 bg-white border border-gray-200 hover:border-cyan-500 hover:bg-cyan-50 rounded-xl px-4 py-4 transition-all cursor-pointer group"
              >
                <div className="w-8 h-8 flex items-center justify-center bg-cyan-500 rounded-lg flex-shrink-0">
                  <i className="ri-arrow-right-line text-white text-base group-hover:translate-x-0.5 transition-transform"></i>
                </div>
                <span className="font-semibold text-[#0f1f3d] text-sm sm:text-base">{rel.name} in Las Vegas</span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
